import { useMemo } from 'react';
import { ConfigProvider, Table } from 'antd';
import type { TablePaginationConfig } from 'antd';
import type { ColumnType, FilterValue, SorterResult, SortOrder } from 'antd/es/table/interface';
import type { ColumnsType } from 'antd/es/table';
import { motion } from 'framer-motion';
import { SOVEREIGN } from '@/features/epc/utils/epcProjectVisual';
import { dataTableTheme } from './dataTableTheme';
import DataTableToolbar from './DataTableToolbar';
import DataTablePagination from './DataTablePagination';
import type { DataTableShellProps } from './types';

function EmptyState({ emptyState }: { emptyState: NonNullable<DataTableShellProps<unknown>['emptyState']> }) {
  return (
    <div className="px-6 py-16 text-center">
      <h3 className="text-base font-extrabold" style={{ color: SOVEREIGN.title }}>{emptyState.title}</h3>
      {emptyState.description && (
        <p className="mx-auto mt-2 max-w-md text-sm" style={{ color: SOVEREIGN.kpiCaption }}>{emptyState.description}</p>
      )}
      {emptyState.action && <div className="mt-6">{emptyState.action}</div>}
    </div>
  );
}

export default function DataTableShell<T extends object>({
  title, subtitle, icon, headerExtra, primaryAction, secondaryActions,
  columns, data, rowKey, loading, emptyState, toolbar, pagination, onSortChange,
  sortField, sortOrder, fitContainer, stickyHeader, bodyMaxHeight, rowClassName,
}: DataTableShellProps<T>) {
  const handleTableChange = (
    _pg: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[],
  ) => {
    if (!onSortChange) return;
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    if (!s || !s.order) return onSortChange(null, null);
    const field = Array.isArray(s.field) ? s.field.join('.') : String(s.field ?? s.columnKey);
    onSortChange(field, s.order === 'ascend' ? 'asc' : 'desc');
  };

  // Controlled sort: reflect the page's sort state on the matching column so the
  // caret survives `columns` array recreation and asc→desc→none toggling works.
  const controlledColumns = useMemo(() => {
    if (sortField === undefined && sortOrder === undefined) return columns;
    const antdOrder: SortOrder = sortOrder === 'asc' ? 'ascend' : sortOrder === 'desc' ? 'descend' : null;
    return columns.map((col) => {
      const c = col as ColumnType<T>;
      if (!c.sorter) return col;
      const key = (c.key ?? c.dataIndex) as string | undefined;
      const order: SortOrder = key != null && key === sortField ? antdOrder : null;
      return { ...col, sortOrder: order };
    }) as ColumnsType<T>;
  }, [columns, sortField, sortOrder]);

  const showEmpty = !loading && data.length === 0 && !!emptyState;
  // position:sticky cannot escape an overflow-hidden ancestor, so the card
  // relaxes its clipping when a pinned header is requested.
  const sticky = stickyHeader === true ? 0 : typeof stickyHeader === 'number' ? stickyHeader : null;

  return (
    <ConfigProvider theme={dataTableTheme}>
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className={`relative rounded-2xl ${sticky == null ? 'overflow-hidden' : 'overflow-visible'}`}
        style={{ background: SOVEREIGN.cardBg, border: `1px solid ${SOVEREIGN.cardBorder}`, boxShadow: SOVEREIGN.cardShadow }}
      >
        <span aria-hidden className="pointer-events-none absolute" style={{ inset: 7, border: `1px dashed ${SOVEREIGN.innerRule}`, borderRadius: 11 }} />

        <header className="relative flex flex-wrap items-center gap-3 px-6 pt-6">
          {icon && (
            <span className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: 'rgba(201,169,97,0.16)', color: SOVEREIGN.bottle }}>
              {icon}
            </span>
          )}
          <div className="mr-auto">
            <h1 className="text-xl font-extrabold tracking-tight" style={{ color: SOVEREIGN.title }}>{title}</h1>
            {subtitle && <p className="text-sm" style={{ color: SOVEREIGN.kpiCaption }}>{subtitle}</p>}
          </div>
          {headerExtra && <div className="flex min-w-0 items-center">{headerExtra}</div>}
        </header>

        {(toolbar || primaryAction || secondaryActions) && (
          <div className="relative px-6 pt-5">
            <DataTableToolbar {...toolbar} primaryAction={primaryAction} secondaryActions={secondaryActions} />
          </div>
        )}

        <div className="relative mt-4">
          {showEmpty ? (
            <EmptyState emptyState={emptyState!} />
          ) : (
            <Table<T>
              rowKey={rowKey as string | ((record: T) => string)}
              columns={controlledColumns}
              dataSource={data}
              loading={loading}
              pagination={false}
              onChange={handleTableChange}
              {...(rowClassName ? { rowClassName } : {})}
              {...(fitContainer ? { tableLayout: 'fixed' as const } : {})}
              {...(() => {
                const scroll: { x?: 'max-content'; y?: number | string } = {};
                if (!fitContainer) scroll.x = 'max-content';
                if (bodyMaxHeight != null) scroll.y = bodyMaxHeight;
                return Object.keys(scroll).length > 0 ? { scroll } : {};
              })()}
              {...(sticky != null ? { sticky: { offsetHeader: sticky } } : {})}
            />
          )}
        </div>

        {pagination && data.length > 0 && (
          <div className="relative flex justify-end px-6 py-4">
            <DataTablePagination {...pagination} />
          </div>
        )}
      </motion.section>
    </ConfigProvider>
  );
}
